/**
 * Data behind the admin day roster: for one date, every program that meets that day —
 * after-school, enrichment, camp — and the children expected in each.
 *
 * The pickup board answers "how is this week's dismissal going" for after-school only. This
 * answers the planning question it cannot: "who is coming next Wednesday", across every
 * program. That question only has an interesting answer for the billing types where children
 * differ day to day — a Flex child's month plan, a per-day camper's booked days — and for
 * enrichment, where what meets depends on the weekday.
 *
 * Fetching and deciding are split: `useAdminDayRoster` runs the queries, `buildDayRoster`
 * is the pure rule that turns rows into groups, so the rule can be tested on its own.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { isSessionDate, NON_TEACHING_TYPES_FILTER } from '../staff-app/programSessions';
import { formatSessionTimeRange, sessionStartMinutes } from '../utils/schedule';
import { compareNamesByFirstName } from '../utils/rosterGrouping';
import { isEnrichmentProgramType } from '../utils/programCalendar';
import { monthStart } from '../utils/attendanceSlot';
import { REAL_SESSION_KEY, type AttendanceStatus } from '../types/attendance';

// ── Row shapes (snake_case, matching the tables) ──────────────────────────────

export interface DayRosterProgram {
  id: string;
  name: string;
  type: string;
  billing_type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
  duration: number | null;
  excluded_dates: string[] | null;
  brand_key: string | null;
}

export interface DayRosterEnrollmentRow {
  id: string;
  student_id: string;
  program_id: string;
  enrollment_start_date: string | null;
  enrollment_end_date: string | null;
  metadata: { selected_days?: unknown } | null;
}

export interface DayRosterStudentRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  active: boolean;
  metadata: {
    school?: string;
    grade?: string;
    room_number?: string;
    bhcs_waiver?: { hasAllergies?: boolean; allergiesDetails?: string };
  } | null;
}

export interface DayRosterFlexPlanRow {
  enrollment_id: string;
  dates: string[] | null;
}

export interface DayRosterAttendanceRow {
  enrollment_id: string;
  status: AttendanceStatus;
  absence_reason: string | null;
}

// ── Output ───────────────────────────────────────────────────────────────────

export type DayRosterKind = 'after-school' | 'enrichment' | 'camp' | 'other';

export interface DayRosterChild {
  studentId: string;
  enrollmentId: string;
  displayName: string;
  grade: string | null;
  school: string | null;
  roomNumber: string | null;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  /** Which program this child is in — only worth showing where a group merges several. */
  programName: string;
  /** A Flex child with no plan on file for the month: may or may not come. */
  flexUnplanned: boolean;
  /** Enrichment only: holds a BHCS after-school seat that day, so walks back after class. */
  inAfterSchool: boolean;
  /** The recorded status that day, when one exists and says something (not `not_arrived`). */
  status: AttendanceStatus | null;
  absenceReason: string | null;
}

export interface DayRosterGroup {
  key: string;
  label: string;
  kind: DayRosterKind;
  programIds: string[];
  /** Enrichment only — see the header of `formatSessionTimeRange`'s callers. */
  timeRange: string | null;
  /** Children expected that day, first-name order. */
  children: DayRosterChild[];
  /**
   * Enrolled, but not coming that day by their own plan: a Flex child who did not pick the
   * day, or a per-day camper who did not book it. Counted, not listed as expected.
   */
  notScheduled: DayRosterChild[];
}

export interface DayRosterData {
  date: string;
  groups: DayRosterGroup[];
  /** Distinct children expected somewhere that day. */
  childCount: number;
}

// ── The rule ─────────────────────────────────────────────────────────────────

function kindOf(type: string): DayRosterKind {
  if (type === 'after-school') return 'after-school';
  if (type === 'camp') return 'camp';
  if (isEnrichmentProgramType(type)) return 'enrichment';
  return 'other';
}

const KIND_ORDER: Record<DayRosterKind, number> = {
  'after-school': 0,
  enrichment: 1,
  camp: 2,
  other: 3,
};

/** Whether an enrollment's own term covers `date`. A NULL bound is open. */
function coversDate(enrollment: DayRosterEnrollmentRow, date: string): boolean {
  if (enrollment.enrollment_start_date && enrollment.enrollment_start_date > date) return false;
  if (enrollment.enrollment_end_date && enrollment.enrollment_end_date < date) return false;
  return true;
}

/** `metadata.selected_days` as a list of YYYY-MM-DD strings; absent or malformed is empty. */
function selectedDaysOf(enrollment: DayRosterEnrollmentRow): string[] {
  const raw = enrollment.metadata?.selected_days;
  if (!Array.isArray(raw)) return [];
  return raw.filter((d): d is string => typeof d === 'string').map(d => d.slice(0, 10));
}

/**
 * Turn one day's rows into roster groups.
 *
 * - The programs are only those that meet on `date` (the caller filters with
 *   `isSessionDate`); every enrollment passed is active and belongs to one of them.
 * - An enrollment counts only if its own term covers `date` — a family whose school starts
 *   later bought a later start against the same program.
 * - `per_day`: expected only on a booked day. `flex_monthly`: expected on a picked day, or
 *   when there is no plan on file for that month (flagged — unknown is not "not coming").
 * - After-school programs of one brand merge into one group, as they do on the teacher
 *   portal and the pickup board: it is one dismissal line whichever plan a family bought.
 *   Every other program is its own group.
 *
 * Exported for testing.
 */
export function buildDayRoster(input: {
  date: string;
  programs: readonly DayRosterProgram[];
  enrollments: readonly DayRosterEnrollmentRow[];
  students: readonly DayRosterStudentRow[];
  flexPlans: readonly DayRosterFlexPlanRow[];
  attendance: readonly DayRosterAttendanceRow[];
}): DayRosterData {
  const { date, programs, enrollments, students, flexPlans, attendance } = input;
  const programById = new Map(programs.map(p => [p.id, p]));
  const studentById = new Map(students.map(s => [s.id, s]));
  const planByEnrollment = new Map(flexPlans.map(p => [p.enrollment_id, p.dates ?? []]));
  const recordByEnrollment = new Map(attendance.map(r => [r.enrollment_id, r]));

  const covering = enrollments.filter(
    e =>
      programById.has(e.program_id) &&
      coversDate(e, date) &&
      // A deactivated student with a stale active enrollment is not coming.
      studentById.get(e.student_id)?.active === true,
  );

  // Who is in BHCS after-school that day — what an enrichment teacher needs to know at the
  // end of the hour. Kaleido seats do not count (BHCS-122).
  const inAfterSchool = new Set(
    covering
      .filter(e => {
        const p = programById.get(e.program_id);
        return p?.type === 'after-school' && (p.brand_key ?? 'bhcs') === 'bhcs';
      })
      .map(e => e.student_id),
  );

  const groups = new Map<string, DayRosterGroup>();
  const groupFor = (program: DayRosterProgram): DayRosterGroup => {
    const kind = kindOf(program.type);
    const brand = program.brand_key ?? 'bhcs';
    const key = kind === 'after-school' ? `after-school:${brand}` : program.id;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label:
          kind === 'after-school'
            ? brand === 'bhcs'
              ? 'After School'
              : `After School (${brand === 'kaleido' ? 'Kaleido' : brand})`
            : program.name,
        kind,
        programIds: [],
        timeRange:
          kind === 'enrichment'
            ? formatSessionTimeRange(program.schedule_cron, program.duration)
            : null,
        children: [],
        notScheduled: [],
      };
      groups.set(key, group);
    }
    if (!group.programIds.includes(program.id)) group.programIds.push(program.id);
    return group;
  };

  // Every program meeting that day gets a group, even an empty one: "chess meets and nobody
  // is enrolled" is itself worth seeing when planning a day.
  for (const program of programs) groupFor(program);

  // One row per student per group: a child with two after-school enrollments (a Flex seat
  // and a Regular seat) is still one child on the line, and expected if either seat says so.
  const placed = new Map<string, { group: DayRosterGroup; child: DayRosterChild; scheduled: boolean }>();
  for (const enrollment of covering) {
    const program = programById.get(enrollment.program_id)!;
    const student = studentById.get(enrollment.student_id)!;
    const group = groupFor(program);

    let scheduled = true;
    let flexUnplanned = false;
    if (program.billing_type === 'per_day') {
      scheduled = selectedDaysOf(enrollment).includes(date);
    } else if (program.billing_type === 'flex_monthly') {
      const plan = planByEnrollment.get(enrollment.id);
      if (plan === undefined) flexUnplanned = true;
      else scheduled = plan.includes(date);
    }

    const key = `${group.key}|${student.id}`;
    const existing = placed.get(key);
    // An expected seat outranks a not-scheduled one; otherwise the first seat stands.
    if (existing && (existing.scheduled || !scheduled)) continue;

    const meta = student.metadata ?? {};
    const record = recordByEnrollment.get(enrollment.id);
    const first = student.first_name?.trim() ?? '';
    const last = student.last_name?.trim() ?? '';
    placed.set(key, {
      group,
      scheduled,
      child: {
        studentId: student.id,
        enrollmentId: enrollment.id,
        displayName: `${first} ${last}`.trim(),
        grade: meta.grade ?? null,
        school: meta.school ?? null,
        roomNumber: meta.room_number ?? null,
        hasAllergies: meta.bhcs_waiver?.hasAllergies === true,
        allergiesDetails: meta.bhcs_waiver?.allergiesDetails ?? null,
        programName: program.name,
        flexUnplanned,
        inAfterSchool: group.kind === 'enrichment' && inAfterSchool.has(student.id),
        status: record && record.status !== 'not_arrived' ? record.status : null,
        absenceReason: record?.absence_reason ?? null,
      },
    });
  }
  for (const { group, child, scheduled } of placed.values()) {
    (scheduled ? group.children : group.notScheduled).push(child);
  }

  const byName = (a: DayRosterChild, b: DayRosterChild) =>
    compareNamesByFirstName(a.displayName, b.displayName);
  const startOf = (group: DayRosterGroup) =>
    Math.min(
      ...group.programIds.map(id => sessionStartMinutes(programById.get(id)?.schedule_cron) ?? 24 * 60),
    );

  const ordered = [...groups.values()]
    .map(group => ({
      ...group,
      children: [...group.children].sort(byName),
      notScheduled: [...group.notScheduled].sort(byName),
    }))
    .sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        startOf(a) - startOf(b) ||
        a.label.localeCompare(b.label),
    );

  const expected = new Set(ordered.flatMap(g => g.children.map(c => c.studentId)));
  return { date, groups: ordered, childCount: expected.size };
}

// ── Queries ──────────────────────────────────────────────────────────────────

export const dayRosterKeys = {
  programs: ['admin-day-roster-programs'] as const,
  day: (date: string) => ['admin-day-roster', date] as const,
};

/**
 * Every active teaching program. Shared by the date picker (which days anything meets)
 * and the day query (which programs meet on the chosen one).
 */
export function useAdminRosterPrograms() {
  return useQuery<DayRosterProgram[]>({
    queryKey: dayRosterKeys.programs,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select(
          'id, name, type, billing_type, schedule_cron, start_date, end_date, duration, excluded_dates, brand_key',
        )
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (error) throw error;
      return (data ?? []) as DayRosterProgram[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useAdminDayRoster(date: string, programs: readonly DayRosterProgram[] | undefined) {
  return useQuery<DayRosterData>({
    queryKey: dayRosterKeys.day(date),
    queryFn: async () => {
      const meeting = (programs ?? []).filter(p => isSessionDate(p, date));
      if (meeting.length === 0) return { date, groups: [], childCount: 0 };
      const programIds = meeting.map(p => p.id);

      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from('enrollments')
        .select('id, student_id, program_id, enrollment_start_date, enrollment_end_date, metadata')
        .in('program_id', programIds)
        .eq('is_active', true);
      if (enrollmentError) throw enrollmentError;
      const enrollments = (enrollmentData ?? []) as DayRosterEnrollmentRow[];

      const studentIds = Array.from(new Set(enrollments.map(e => e.student_id)));
      const flexIds = enrollments
        .filter(e => meeting.find(p => p.id === e.program_id)?.billing_type === 'flex_monthly')
        .map(e => e.id);

      const [studentsRes, flexRes, attendanceRes] = await Promise.all([
        studentIds.length > 0
          ? supabase
              .from('students')
              .select('id, first_name, last_name, active, metadata')
              .in('id', studentIds)
          : Promise.resolve({ data: [], error: null }),
        flexIds.length > 0
          ? supabase
              .from('flex_month_plans')
              .select('enrollment_id, dates')
              .in('enrollment_id', flexIds)
              .eq('plan_month', monthStart(date))
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('attendance_records')
          .select('enrollment_id, status, absence_reason')
          .eq('session_date', date)
          .in('program_id', programIds)
          // Real sessions only: a virtual Chinese or Math row borrows the after-school
          // program_id and enrollment, and must not stand in for the after-school record.
          .eq('virtual_class_key', REAL_SESSION_KEY),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      if (flexRes.error) throw flexRes.error;
      if (attendanceRes.error) throw attendanceRes.error;

      return buildDayRoster({
        date,
        programs: meeting,
        enrollments,
        students: (studentsRes.data ?? []) as DayRosterStudentRow[],
        flexPlans: (flexRes.data ?? []) as DayRosterFlexPlanRow[],
        attendance: (attendanceRes.data ?? []) as DayRosterAttendanceRow[],
      });
    },
    enabled: programs !== undefined,
    staleTime: 60_000,
  });
}
